/**
 * SubCategoriesTableV2 — Subcategories list, ported from the prototype's
 * `.screen-subcategories` (cms-redesign-mockup.html ~line 6935+, styling
 * shares the `.lib-section` / `.lib-table` / `.sub-title-cell` /
 * `.parent-cat-chip` blocks with the Categories page).
 *
 * Layout: two sections, mirroring the v1 split:
 *   1. Audio Subcategories     — !is_quick_tap && !is_book
 *   2. Quick Tap Subcategories —  is_quick_tap
 *
 * Audiobook subcategories (`subcategory_is_book`) are excluded — they're
 * managed under the Audiobooks page (v1 parity: SubCategoriesTable.js:90).
 *
 * Sorting within each section: active first, then by parent category
 * title (matches v1 SubCategoriesTable.js:104–115).
 *
 * Each section has its own "Filter by category" dropdown — selecting a
 * parent narrows that section to only its children. Sections are
 * independent because the FK is type-scoped (audio subs can't belong to
 * a quick-tap category and vice versa).
 *
 * No drag-to-reorder here — subcategories aren't user-sortable in v1.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchAllSubCategories,
  clearAllSubCategories,
} from '../../../actions/subcategoryActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import {
  LibTable,
  LibSection,
  StatusPill,
  IconButton,
  cn,
} from '../../redesign';

/* ─── Icons (inline; brand pack has no edit/pencil glyph) ───────────── */
const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Parent category chip ─────────────────────────────────────────────
 *  `.parent-cat-chip` (prototype line 1967–1992): cream pill with a 18px
 *  circular parent thumbnail + parent name, navigates to the parent on
 *  click. Falls back to a muted "No category" chip when the FK is
 *  missing (orphaned subcategory). */
function ParentCategoryChip({ category }) {
  if (!category) {
    return (
      <span
        className="inline-flex items-center text-text-muted bg-cream border border-sand rounded-pill"
        style={{ fontSize: 12, padding: '3px 10px', gap: 6 }}
      >
        No category
      </span>
    );
  }
  return (
    <Link
      to={`/category/edit/${category.category_id}`}
      className={cn(
        'inline-flex items-center bg-cream border border-sand rounded-pill text-navy',
        'hover:bg-surface hover:border-teal-green hover:text-teal-green-dark',
        'no-underline max-w-full',
      )}
      style={{
        fontSize: 12,
        padding: '3px 10px 3px 6px',
        gap: 6,
        transition: 'all 140ms ease',
      }}
    >
      <span
        className="shrink-0 bg-sand"
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundImage: category.category_image
            ? `url('${category.category_image}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <span className="truncate min-w-0">
        {category.category_title || 'Untitled'}
      </span>
    </Link>
  );
}

/* ─── Filter dropdown ──────────────────────────────────────────────── */
function CategoryFilterSelect({ id, value, onChange, categories }) {
  return (
    <div
      className="flex items-center"
      style={{ gap: 10, marginBottom: 10 }}
    >
      <label
        htmlFor={id}
        className="font-semibold text-text-muted"
        style={{ fontSize: 12 }}
      >
        Filter by category:
      </label>
      {/* `.sub-filter-bar select` (prototype 1855–1868): 32px h, sand
       *  border, md radius, surface bg, inline-SVG caret. */}
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-surface text-navy border border-sand cursor-pointer"
        style={{
          height: 32,
          padding: '0 28px 0 10px',
          borderRadius: 8,
          fontSize: 13,
          appearance: 'none',
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2384787C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center',
          backgroundSize: 14,
        }}
      >
        <option value="">All categories</option>
        {categories.map((c) => (
          // Value is the title — the subcategory list response doesn't
          // expose category_id, so we match by title for the filter too.
          <option key={c.category_id} value={c.category_title}>
            {c.category_title}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ─── Subcategory row ──────────────────────────────────────────────── */
function SubcategoryRow({ subcategory, parentCategory }) {
  const inactive = !subcategory.subcategory_is_active;
  return (
    <LibTable.Row inactive={inactive}>
      <LibTable.Td>
        {/* `.sub-title-cell` — text-only (no subcategory_image in the API). */}
        <span className="font-semibold text-navy truncate" style={{ fontSize: 13.5 }}>
          {subcategory.subcategory_title || 'Untitled'}
        </span>
      </LibTable.Td>
      <LibTable.Td>
        <ParentCategoryChip category={parentCategory} />
      </LibTable.Td>
      <LibTable.CenterTd>
        <StatusPill active={!inactive} />
      </LibTable.CenterTd>
      <LibTable.NumTd muted={!Number(subcategory.subcategory_num_sessions)}>
        {Number(subcategory.subcategory_num_sessions) || 0}
      </LibTable.NumTd>
      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link
            to={`/subcategories/edit/${subcategory.subcategory_id}`}
            aria-label={`Edit ${subcategory.subcategory_title}`}
          >
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── One section (with its own filter dropdown) ──────────────────── */
function SubcategorySection({
  title,
  subcategories,
  parentMap,
  parentCategories,
  filterId,
  filterValue,
  onFilterChange,
  first,
}) {
  return (
    <LibSection
      title={title}
      count={subcategories.length}
      hint="Sorted by status, then parent category"
      first={first}
    >
      {parentCategories.length > 0 && (
        <CategoryFilterSelect
          id={filterId}
          value={filterValue}
          onChange={onFilterChange}
          categories={parentCategories}
        />
      )}
      {subcategories.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{ borderRadius: 12, padding: '20px 16px', fontSize: 13 }}
        >
          {filterValue
            ? 'No subcategories under this category.'
            : 'No subcategories in this section yet.'}
        </div>
      ) : (
        <LibTable
          fixed
          className="border border-sand"
          style={{ borderRadius: 12, overflow: 'hidden' }}
        >
          <colgroup>
            <col />
            <col style={{ width: '32%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <LibTable.Head>
            <tr>
              <LibTable.Th>Subcategory</LibTable.Th>
              <LibTable.Th>Category</LibTable.Th>
              <LibTable.CenterTh>Status</LibTable.CenterTh>
              <LibTable.NumTh>Sessions</LibTable.NumTh>
              <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
            </tr>
          </LibTable.Head>
          <tbody>
            {subcategories.map((s) => (
              <SubcategoryRow
                key={s.subcategory_id}
                subcategory={s}
                parentCategory={parentMap[s.category_title]}
              />
            ))}
          </tbody>
        </LibTable>
      )}
    </LibSection>
  );
}

/* ─── Main component ──────────────────────────────────────────────── */

function compareForSection(a, b) {
  // Active first, then by parent category title (v1 parity).
  if (a.subcategory_is_active !== b.subcategory_is_active) {
    return b.subcategory_is_active - a.subcategory_is_active;
  }
  return (a.category_title || 'ZZZ').localeCompare(b.category_title || 'ZZZ');
}

export default function SubCategoriesTableV2() {
  const dispatch = useDispatch();
  const allSubcategories = useSelector((s) => s.subcategories.subcategories);
  const allCategories    = useSelector((s) => s.categories.all_categories);

  // Per-section filter — null/'' = all categories.
  const [audioFilter, setAudioFilter]       = useState('');
  const [quickTapFilter, setQuickTapFilter] = useState('');

  // Subcategories fetch — defaults to is_audiobook=0, matching v1.
  useEffect(() => {
    dispatch(clearAllSubCategories());
    dispatch(fetchAllSubCategories(0));
  }, [dispatch]);

  // Categories fetch — only fires when not already loaded. Used for the
  // parent-chip thumbnails + the per-section "Filter by category" select.
  // Split into its own effect so the deps list stays honest and doesn't
  // re-trigger a refetch whenever allCategories changes.
  useEffect(() => {
    if (!allCategories) dispatch(fetchAllCategories());
  }, [dispatch, allCategories]);

  // Reducer keys subcategories by id → flatten to an array.
  const subcategoryList = useMemo(
    () => (allSubcategories ? Object.values(allSubcategories) : null),
    [allSubcategories],
  );

  // category_title → category object. The subcategory list API
  // (SubcategoryListResource per cms-api-openapi.yaml line 4232)
  // doesn't include the parent's `category_id` — it only ships the
  // denormalized `category_title`. So we match parents by title.
  // Title collisions are rare in practice; if two categories share a
  // title, the first wins (which is what v1 effectively does too, since
  // it just prints `category_title` without lookup).
  const parentMap = useMemo(() => {
    const map = {};
    if (allCategories) {
      for (const c of allCategories) {
        if (c.category_title) map[c.category_title] = c;
      }
    }
    return map;
  }, [allCategories]);

  // Per-section parent options. Audio subcategories can only have
  // !is_quick_tap parents, Quick Tap subs only is_quick_tap parents.
  const audioParents = useMemo(
    () => (allCategories || [])
      .filter((c) => !c.category_is_audiobook && !c.category_is_quick_tap)
      .sort((a, b) => (a.category_title || '').localeCompare(b.category_title || '')),
    [allCategories],
  );
  const quickTapParents = useMemo(
    () => (allCategories || [])
      .filter((c) => !c.category_is_audiobook && c.category_is_quick_tap)
      .sort((a, b) => (a.category_title || '').localeCompare(b.category_title || '')),
    [allCategories],
  );

  // Audio + Quick Tap buckets, filtered + sorted.
  // Filters match by `category_title` (the only parent identifier on the
  // subcategory list response). See parentMap above.
  const audio = useMemo(() => {
    if (!subcategoryList) return [];
    return subcategoryList
      .filter((s) => !s.subcategory_is_book && !s.subcategory_is_quick_tap)
      .filter((s) => !audioFilter || s.category_title === audioFilter)
      .sort(compareForSection);
  }, [subcategoryList, audioFilter]);

  const quickTap = useMemo(() => {
    if (!subcategoryList) return [];
    return subcategoryList
      .filter((s) => !s.subcategory_is_book && s.subcategory_is_quick_tap)
      .filter((s) => !quickTapFilter || s.category_title === quickTapFilter)
      .sort(compareForSection);
  }, [subcategoryList, quickTapFilter]);

  if (!subcategoryList) {
    return (
      <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
        Loading subcategories…
      </div>
    );
  }

  return (
    <div style={{ padding: '0 28px 32px' }}>
      <SubcategorySection
        first
        title="Audio Subcategories"
        subcategories={audio}
        parentMap={parentMap}
        parentCategories={audioParents}
        filterId="sub-cat-filter-audio"
        filterValue={audioFilter}
        onFilterChange={setAudioFilter}
      />
      <SubcategorySection
        title="Quick Tap Subcategories"
        subcategories={quickTap}
        parentMap={parentMap}
        parentCategories={quickTapParents}
        filterId="sub-cat-filter-qtap"
        filterValue={quickTapFilter}
        onFilterChange={setQuickTapFilter}
      />
    </div>
  );
}
